import React, { useEffect, useState, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";

import FormFooter from "shared/components/FormElements/FormFooter";
import CheckBox from "shared/components/FormElements/CheckBox";

import Input from "shared/components/FormElements/Input";
import Button from "shared/components/FormElements/Button";
import CustomSelect from "shared/components/FormElements/Select";
import Modal from "shared/components/UIElements/Modal";
import { VALIDATOR_MAX, VALIDATOR_MIN, VALIDATOR_NONE } from "shared/util/validators";

import { booleanstatus_tr, boolenstatus_en } from "shared/util/types";

import { useForm } from "shared/hooks/form-hook";
import { StoreContext } from "shared/context/store-context";
import { getCurrentLanguage } from "shared/utility/lang";
import { AuthContext } from "shared/context/auth-context";
import { COMPANY_FEATURES } from "@routebot/constants";

const RelationRow = ({ onDelete, onChange, onInit, parentId, studentId = null }) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);

  const showServistesinFeatures = auth.company.features[COMPANY_FEATURES.PHONE_APP];

  const currentLanguage = getCurrentLanguage();
  const booleanstatus = currentLanguage === "en" ? boolenstatus_en : booleanstatus_tr;

  const { relations, userMap } = useContext(StoreContext);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);

  const [currentParent, setCurrentParent] = useState(null);

  const [formState, inputHandler, setFormData] = useForm(
    {
      //inputs
      relationId: { value: null, isValid: true },
      isenabled: { value: null, isValid: true },
      approachtogeton: { value: null, isValid: true },
      approachtogetoff: { value: null, isValid: true },
      arrivedschool: { value: null, isValid: true },
      leavedschool: { value: null, isValid: true },
      distancegeton: { value: null, isValid: true },
      distancegetoff: { value: null, isValid: true },
      studentnotgeton: { value: null, isValid: true },
    },
    true
  );

  useEffect(() => {
    const relation = relations.find((r) => r.parent === parentId && r.student === studentId);

    if (!relation) {
      setFormData(
        {
          relationId: { value: null, isValid: true },
          isenabled: { value: true, isValid: true },
          approachtogeton: { value: true, isValid: true },
          approachtogetoff: { value: true, isValid: true },
          arrivedschool: { value: true, isValid: true },
          leavedschool: { value: true, isValid: true },
          studentnotgeton: { value: true, isValid: true },
          distancegeton: { value: 2, isValid: true },
          distancegetoff: { value: 2, isValid: true },
        },
        true
      );
    } else {
      setFormData(
        {
          relationId: { value: relation.id, isValid: true },
          isenabled: { value: relation.isenabled, isValid: true },
          approachtogeton: { value: relation.approachtogeton, isValid: true },
          approachtogetoff: { value: relation.approachtogetoff, isValid: true },
          arrivedschool: { value: relation.arrivedschool, isValid: true },
          leavedschool: { value: relation.leavedschool, isValid: true },
          distancegeton: { value: relation.distancegeton, isValid: true },
          distancegetoff: { value: relation.distancegetoff, isValid: true },
          studentnotgeton: { value: relation.studentnotgeton, isValid: true },
        },
        true
      );
    }

    setCurrentParent(userMap[parentId] || null);

    onInit({
      relationId: relation ? relation.id : null,
      parent: parentId,
      student: studentId,
      isenabled: relation ? relation.isenabled : true,
      approachtogeton: relation ? relation.approachtogeton : true,
      approachtogetoff: relation ? relation.approachtogetoff : true,
      arrivedschool: relation ? relation.arrivedschool : true,
      leavedschool: relation ? relation.leavedschool : true,
      distancegeton: relation ? relation.distancegeton : 2,
      distancegetoff: relation ? relation.distancegetoff : 2,
      studentnotgeton: relation ? relation.studentnotgeton : true,
    });
  }, [parentId, relations, studentId, userMap, setFormData]);

  const handleCheckboxChange = useCallback(
    (id, value, isValid) => {
      // Only proceed if the value actually changed
      if (formState.inputs.isenabled.value !== value) {
        inputHandler(id, value, isValid);
        // Call onChange with the new value
        onChange({
          relationId: formState.inputs.relationId.value,
          parent: parentId,
          student: studentId,
          isenabled: value,
          approachtogeton: formState.inputs.approachtogeton.value,
          approachtogetoff: formState.inputs.approachtogetoff.value,
          arrivedschool: formState.inputs.arrivedschool.value,
          leavedschool: formState.inputs.leavedschool.value,
          distancegeton: formState.inputs.distancegeton.value,
          distancegetoff: formState.inputs.distancegetoff.value,
          studentnotgeton: formState.inputs.studentnotgeton.value,
        });
      }
    },
    [
      formState.inputs.isenabled.value,
      onChange,
      parentId,
      studentId,
      inputHandler,
      formState.inputs.relationId.value,
      formState.inputs.approachtogeton.value,
      formState.inputs.approachtogetoff.value,
      formState.inputs.arrivedschool.value,
      formState.inputs.leavedschool.value,
      formState.inputs.distancegeton.value,
      formState.inputs.distancegetoff.value,
      formState.inputs.studentnotgeton.value,
    ]
  );

  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();
    onChange({
      relationId: formState.inputs.relationId.value,
      parent: parentId,
      student: studentId,
      isenabled: formState.inputs.isenabled.value,
      approachtogeton: formState.inputs.approachtogeton.value,
      approachtogetoff: formState.inputs.approachtogetoff.value,
      arrivedschool: formState.inputs.arrivedschool.value,
      leavedschool: formState.inputs.leavedschool.value,
      distancegeton: formState.inputs.distancegeton.value,
      distancegetoff: formState.inputs.distancegetoff.value,
      studentnotgeton: formState.inputs.studentnotgeton.value,
    });
    setShowNotificationsModal(false);
  };

  return (
    <div>
      {currentParent && currentParent.id ? (
        <div
          className="horizontal-flex"
          style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}
        >
          <Button danger type="button" size="small" onClick={() => onDelete(parentId)}>
            {t("removeParent")}
          </Button>
          <span>{currentParent.name + (currentParent.phonenumber ? " (" + currentParent.phonenumber + ")" : "")}</span>
          {showServistesinFeatures && (
            <CheckBox
              key={parentId + "relation" + formState.inputs.isenabled.value} // burası önemli
              id="isenabled"
              label={t("notifications")}
              initialValue={formState.inputs.isenabled.value}
              initialValid={true}
              onInput={handleCheckboxChange}
              validators={[VALIDATOR_NONE()]}
              labelStyle={{ marginTop: "0" }}
            />
          )}

          {formState.inputs.isenabled.value && showServistesinFeatures && (
            <div>
              <Button
                type="button"
                size="small"
                containerStyle={{ display: "flex", justifyContent: "flex-end" }}
                onClick={() => {
                  setShowNotificationsModal(true);
                }}
              >
                {t("notificationSettings")}
              </Button>

              <Modal
                show={showNotificationsModal}
                onCancel={() => setShowNotificationsModal(false)}
                header={t("notificationSettings")}
              >
                <form className="form-container" onSubmit={itemUpdateSubmitHandler}>
                  <div className="horizontal-flex">
                    {/* <CustomSelect
                      options={booleanstatus}
                      id="isenabled"
                      onInput={inputHandler}
                      label={t("notificationsStatus")}
                      initialValid={true}
                      initialValue={
                        booleanstatus.find((option) => formState.inputs?.isenabled.value === option.value) ||
                        booleanstatus[1]
                      }
                    ></CustomSelect> */}

                    <CustomSelect
                      options={booleanstatus}
                      id="approachtogeton"
                      onInput={inputHandler}
                      label={t("whenApproachToGetOn")}
                      initialValid={true}
                      initialValue={
                        booleanstatus.find(
                          (option) => formState.inputs?.approachtogeton.value.toString() === option.value.toString()
                        ) || booleanstatus[1]
                      }
                    ></CustomSelect>
                  </div>

                  <div className="horizontal-flex">
                    <CustomSelect
                      options={booleanstatus}
                      id="approachtogetoff"
                      onInput={inputHandler}
                      label={t("whenApproachToGetOff")}
                      initialValid={true}
                      initialValue={
                        booleanstatus.find(
                          (option) => formState.inputs.approachtogetoff.value.toString() === option.value.toString()
                        ) || booleanstatus[1]
                      }
                    ></CustomSelect>

                    <CustomSelect
                      options={booleanstatus}
                      id="arrivedschool"
                      onInput={inputHandler}
                      label={t("whenArrivedAtTheDestination")}
                      initialValid={true}
                      initialValue={
                        booleanstatus.find(
                          (option) => formState.inputs?.arrivedschool.value.toString() === option.value.toString()
                        ) || booleanstatus[1]
                      }
                    ></CustomSelect>
                  </div>

                  <div className="horizontal-flex">
                    <CustomSelect
                      options={booleanstatus}
                      id="leavedschool"
                      onInput={inputHandler}
                      label={t("whenDropOffStarted")}
                      initialValid={true}
                      initialValue={
                        booleanstatus.find(
                          (option) => formState.inputs.leavedschool.value.toString() === option.value.toString()
                        ) || booleanstatus[1]
                      }
                    ></CustomSelect>

                    <CustomSelect
                      options={booleanstatus}
                      id="studentnotgeton"
                      onInput={inputHandler}
                      label={t("whenNotGetOnTheBus")}
                      initialValid={true}
                      initialValue={
                        booleanstatus.find(
                          (option) => formState.inputs.studentnotgeton.value.toString() === option.value.toString()
                        ) || booleanstatus[1]
                      }
                    ></CustomSelect>
                  </div>

                  <div className="horizontal-flex">
                    <Input
                      id="distancegeton"
                      element="input"
                      type="text"
                      label={t("distancegeton")}
                      validators={[VALIDATOR_MAX(5), VALIDATOR_MIN(0.1)]}
                      initialValue={formState.inputs?.distancegeton.value || 1.9}
                      initialValid={true}
                      onInput={inputHandler}
                      errorText={t("distancegetonErrorText")}
                    />

                    <Input
                      id="distancegetoff"
                      element="input"
                      type="text"
                      label={t("distancegetoff")}
                      validators={[VALIDATOR_MAX(5), VALIDATOR_MIN(0.1)]}
                      initialValue={formState.inputs?.distancegetoff.value || 1.9}
                      initialValid={true}
                      onInput={inputHandler}
                      errorText={t("distancegetonErrorText")}
                    />
                  </div>
                  <FormFooter
                    isNewItem={true}
                    cta={t("save")}
                    onUpdate={itemUpdateSubmitHandler}
                    onDelete={() => {}}
                    isUpdating={false}
                    disabled={!formState.isValid}
                  />
                </form>
              </Modal>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default RelationRow;
